import React, { Suspense, lazy, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityLogger } from '../lib/activityLogger.js';
import FamilyMembersShowcase from '../components/FamilyMembersShowcase.jsx';

const Header = lazy(() => import('../components/Header.jsx'));
const Dashboard = lazy(() => import('../components/Dashboard.jsx'));
const CrownReveal = lazy(() => import('../components/CrownReveal.jsx'));
const EyeWatcher = lazy(() => import('../components/EyeWatcher.jsx'));
const Settings = lazy(() => import('../components/Settings.jsx'));
const FozzilAI = lazy(() => import('../components/FozzilAI.jsx'));
const TimeCapsule = lazy(() => import('../components/TimeCapsule.jsx'));

function AmbientAudio({ active }) {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!active || !window.AudioContext) {
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const gain = ctx.createGain();
    gain.gain.value = 0.06;
    gain.connect(ctx.destination);
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 220;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 12;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    osc.connect(gain);
    osc.start();
    lfo.start();
    const timers = [];
    function scheduleChime() {
      const now = ctx.currentTime + Math.random() * 3 + 1;
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = 880 + Math.random() * 220;
      const g = ctx.createGain();
      g.gain.value = 0;
      o.connect(g);
      g.connect(gain);
      o.start(now);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.12, now + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + 3);
      o.stop(now + 4);
    }
    for (let i = 0; i < 6; i++) {
      timers.push(setInterval(scheduleChime, 6000 + Math.random() * 12000));
    }
    return () => {
      osc.stop();
      lfo.stop();
      timers.forEach(t => clearInterval(t));
      ctx.close();
      setIsPlaying(false);
    };
  }, [active]);

  if (!isPlaying) return null;
  return (
    <div style={{
      position: 'fixed',
      bottom: 80,
      left: 20,
      background: 'rgba(201,166,43,0.08)',
      border: '1px solid rgba(201,166,43,0.2)',
      padding: '8px 12px',
      borderRadius: 8,
      color: '#c9a62b',
      fontSize: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      zIndex: 1500
    }}>
      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#c9a62b', animation: 'blink 2s ease-in-out infinite' }}></span>
      Ambient Audio Active
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ ambient: false, pulse: true, pulseSpeed: 'normal', timeCapsule: false });
  const [showEcho, setShowEcho] = useState(false);
  const [triggerWink, setTriggerWink] = useState(false);

  // Log page view on mount
  useEffect(() => {
    ActivityLogger.pageView('Home Page', '/');
  }, []);

  // Inactivity echo
  useEffect(() => {
    let t = null;
    function reset() {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        const shown = sessionStorage.getItem('jk_echo_shown');
        if (!shown) {
          setShowEcho(true);
          sessionStorage.setItem('jk_echo_shown', '1');
          setTimeout(() => setShowEcho(false), 9000);
        }
      }, 300000);
    }
    ['mousemove', 'keydown', 'scroll'].forEach(ev => window.addEventListener(ev, reset));
    reset();
    return () => {
      ['mousemove', 'keydown', 'scroll'].forEach(ev => window.removeEventListener(ev, reset));
      if (t) clearTimeout(t);
    };
  }, []);

  // Key combo for time capsule
  useEffect(() => {
    function onKey(e) {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'l') {
        setSettings(s => ({ ...s, timeCapsule: !s.timeCapsule }));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleCategorySelect = (categoryId) => {
    const slug = categoryId.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-');
    
    // Log category view
    ActivityLogger.categoryView(categoryId);
    
    navigate(`/category/${slug}`);
  };

  return (
    <div className={`container ${settings.pulse ? 'legacy-pulse' : ''} ${settings.pulse && settings.pulseSpeed === 'fast' ? 'fast' : ''} ${settings.pulse && settings.pulseSpeed === 'slow' ? 'slow' : ''}`} style={{ marginTop: 0, paddingTop: 0 }}>
      <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading Le</div>}>
        <Header />
        <Dashboard onSelect={handleCategorySelect} active={null} />

        <Settings settings={settings} setSettings={setSettings} />

        {/* Family Members Showcase Section */}
        <FamilyMembersShowcase />

        {/* Feature Cards Section */}
        <section style={{ marginTop: 64, marginBottom: 48 }}>
          <h2 style={{ textAlign: 'center', color: '#c9a62b', fontSize: 32, marginBottom: 40 }}>
            Preserve Our Family's Story
          </h2>
          
          <div className="feature-cards-grid">
            {/* Feature Card 1 - Encrypted Vault */}
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#c9a62b" strokeWidth="1.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" fill="rgba(201,166,43,0.2)" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="16" r="1" fill="#c9a62b"/>
                </svg>
              </div>
              <h3 className="feature-title">Encrypted Vault</h3>
              <p className="feature-description">
                Bank-grade AES-256 encryption protects your family's most precious documents, 
                photos, and voice memos. Only you hold the keys to your legacy.
              </p>
              <div className="feature-stats">
                <span className="stat-item">
                  <strong>256-bit</strong> Encryption
                </span>
                <span className="stat-item">
                  <strong>Zero-knowledge</strong> Security
                </span>
              </div>
            </div>

            {/* Feature Card 2 - Timeline Archive */}
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#c9a62b" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="rgba(201,166,43,0.2)"/>
                  <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="8" y1="13" x2="16" y2="13" strokeLinecap="round"/>
                  <line x1="8" y1="17" x2="13" y2="17" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="feature-title">Timeline Archive</h3>
              <p className="feature-description">
                Chronicle your family's journey through generations. Every milestone, every memory, 
                beautifully preserved in an interactive timeline that grows with your legacy.
              </p>
              <div className="feature-stats">
                <span className="stat-item">
                  <strong>Unlimited</strong> Events
                </span>
                <span className="stat-item">
                  <strong>Multi-media</strong> Support
                </span>
              </div>
            </div>

            {/* Feature Card 3 - Property Management */}
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9l9-4 9 4v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="#c9a62b" strokeWidth="1.5" fill="rgba(201,166,43,0.2)"/>
                  <path d="M9 22V12h6v10M12 2L3 9M12 2l9 7" stroke="#c9a62b" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1.5" fill="#c9a62b"/>
                </svg>
              </div>
              <h3 className="feature-title">Estate Management</h3>
              <p className="feature-description">
                Organize properties, holdings, and trusts in one secure location. 
                Attach documents, blueprints, and memories to each asset for future generations.
              </p>
              <div className="feature-stats">
                <span className="stat-item">
                  <strong>7</strong> Categories
                </span>
                <span className="stat-item">
                  <strong>Per-property</strong> Memos
                </span>
              </div>
            </div>

            {/* Feature Card 4 - Voice Memories */}
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="2" width="6" height="14" rx="3" stroke="#c9a62b" strokeWidth="1.5" fill="rgba(201,166,43,0.2)"/>
                  <path d="M5 10v2a7 7 0 0014 0v-2" stroke="#c9a62b" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M12 19v3M8 22h8" stroke="#c9a62b" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="feature-title">Voice Memories</h3>
              <p className="feature-description">
                Capture stories in your own voice. Record audio memos for each property, 
                sharing wisdom and memories that text alone cannot convey.
              </p>
              <div className="feature-stats">
                <span className="stat-item">
                  <strong>Encrypted</strong> Audio
                </span>
                <span className="stat-item">
                  <strong>Unlimited</strong> Recording
                </span>
              </div>
            </div>

            {/* Feature Card 5 - Private & Secure */}
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#c9a62b" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" stroke="#c9a62b"/>
                  <circle cx="9" cy="10" r="1.5" fill="#c9a62b"/>
                  <circle cx="15" cy="10" r="1.5" fill="#c9a62b"/>
                  <path d="M8 15c.5 1.5 2 2.5 4 2.5s3.5-1 4-2.5" strokeLinecap="round"/>
                  <path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round" opacity="0.5"/>
                </svg>
              </div>
              <h3 className="feature-title">Absolute Privacy</h3>
              <p className="feature-description">
                No external servers, no analytics, no tracking. Your family's legacy stays 
                in your control, protected by client-side encryption and local storage.
              </p>
              <div className="feature-stats">
                <span className="stat-item">
                  <strong>No</strong> Cloud Sync
                </span>
                <span className="stat-item">
                  <strong>100%</strong> Private
                </span>
              </div>
            </div>

            {/* Feature Card 6 - Time Capsule */}
            <div className="feature-card">
              <div className="feature-icon">⏳</div>
              <h3 className="feature-title">Time Capsule</h3>
              <p className="feature-description">
                Create snapshots of your legacy at any moment in time. Preserve versions 
                for future discovery and reflection across generations.
              </p>
              <div className="feature-stats">
                <span className="stat-item">
                  <strong>Version</strong> History
                </span>
                <span className="stat-item">
                  <strong>Ctrl+Shift+L</strong> to Toggle
                </span>
              </div>
            </div>
          </div>
        </section>

        <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
        <Suspense fallback={null}><CrownReveal /></Suspense>
        <Suspense fallback={null}><FozzilAI /></Suspense>
        <Suspense fallback={null}><TimeCapsule active={settings.timeCapsule} /></Suspense>

        {settings.ambient && <AmbientAudio active={settings.ambient} />}
        {showEcho && <div className="legacy-echo">Remember what he said… "Legacy is not inherited — it's built."</div>}
      </Suspense>
    </div>
  );
}
